// src/pages/admin/edit-taller.tsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { calcularEstadoTaller } from '../../utils/tallerEstadoHelper';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PageTitle from '../../components/page-title';
import { LoadingMessage } from '../../components/loading-states';

const schema = Yup.object().shape({
  nombre: Yup.string().required('El nombre del evento es obligatorio'),
  fecha: Yup.date().required('La fecha del evento es obligatoria'),
  horaInicio: Yup.string().required('La hora de inicio es obligatoria'),
  horaTermino: Yup.string().required('La hora de término es obligatoria'),
  lugar: Yup.string().required('El lugar es obligatorio'),
  direccion: Yup.string().required('La dirección es obligatoria'),
  ciudad: Yup.string().required('La ciudad es obligatoria'),
  organizadorPrincipal: Yup.string().required('El organizador es obligatorio'),
  responsableNombre: Yup.string().required('El nombre del responsable es obligatorio'),
  responsableCargo: Yup.string().required('El cargo del responsable es obligatorio'),
  responsableCorreo: Yup.string().email('Correo inválido'),
  responsableTelefono: Yup.string(),
  tipoEvento: Yup.string().required('El tipo de evento es obligatorio'),
  enlaceVirtual: Yup.string().url('Debe ser un enlace válido (Zoom, Teams, etc.)'),
  objetivo: Yup.string().required('El objetivo es obligatorio'),
  publicoObjetivo: Yup.string().required('El público objetivo es obligatorio'),
  descripcionGeneral: Yup.string().required('La descripción es obligatoria'),
  cupoMaximo: Yup.number().positive().required('El cupo es obligatorio'),
  tipoCosto: Yup.string().required('El tipo de costo es obligatorio'),
  precio: Yup.number().min(0),
  moneda: Yup.string(),
  creditos: Yup.number().min(0)
});

export default function EditTaller() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [taller, setTaller] = useState<any>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [currentBannerUrl, setCurrentBannerUrl] = useState<string>('');
  const [estadoManual, setEstadoManual] = useState<string>('');
  const [tipoEventoActual, setTipoEventoActual] = useState<string>('presencial');
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    const fetchTaller = async () => {
      if (!id) return;
      const docRef = doc(db, 'taller', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setTaller(data);

        // Llenar el formulario con los datos existentes
        setValue('nombre', data.nombre);

        // Manejar fecha (puede ser campo fecha o fechaInicio)
        if (data.fecha) {
          setValue('fecha', data.fecha.toDate().toISOString().split('T')[0]);
        } else if (data.fechaInicio) {
          setValue('fecha', data.fechaInicio.toDate().toISOString().split('T')[0]);
        }

        setValue('horaInicio', data.horaInicio || data.horario?.split('-')[0]?.trim() || '09:00');
        setValue('horaTermino', data.horaTermino || data.horario?.split('-')[1]?.trim() || '18:00');
        setValue('lugar', data.lugar);
        setValue('direccion', data.direccion || '');
        setValue('ciudad', data.ciudad || '');
        setValue('organizadorPrincipal', data.organizadorPrincipal || data.expositor || '');

        // Datos del responsable
        if (data.responsable) {
          setValue('responsableNombre', data.responsable.nombre);
          setValue('responsableCargo', data.responsable.cargo);
          setValue('responsableCorreo', data.responsable.correo || '');
          setValue('responsableTelefono', data.responsable.telefono || '');
        } else {
          setValue('responsableNombre', '');
          setValue('responsableCargo', '');
        }

        setValue('tipoEvento', data.tipoEvento || 'presencial');
        setTipoEventoActual(data.tipoEvento || 'presencial');
        setValue('enlaceVirtual', data.enlaceVirtual || '');
        setValue('objetivo', data.objetivo || '');
        setValue('publicoObjetivo', data.publicoObjetivo || '');
        setValue('descripcionGeneral', data.descripcionGeneral || '');
        setValue('cupoMaximo', data.cupoMaximo || data.cupo || 50);
        setValue('tipoCosto', data.tipoCosto || 'gratuito');
        setValue('precio', data.precio || 0);
        setValue('moneda', data.moneda || 'MXN');
        setValue('creditos', data.creditos || 0);

        // Establecer estado actual
        setEstadoManual(data.estado || 'proximo');

        // Guardar URL del banner actual si existe
        if (data.bannerUrl) {
          setCurrentBannerUrl(data.bannerUrl);
        }
      }
      setLoading(false);
    };
    fetchTaller();
  }, [id, setValue]);

  const onSubmit = async (data: any) => {
    if (!id) return;
    const docRef = doc(db, 'taller', id);

    try {
      // Subir banner si hay uno nuevo
      let bannerUrl = currentBannerUrl;
      if (bannerFile) {
        const formData = new FormData();
        formData.append('nombreTaller', data.nombre);
        formData.append('fecha', data.fecha);
        formData.append('banner', bannerFile);

        const res = await fetch(process.env.REACT_APP_UPLOAD_BANNER_URL || 'https://ampirivieranayarit.com/modulob/backend/upload-banner.php', {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          const json = await res.json();
          bannerUrl = json.url;
        }
      }

      // Calcular estado automático si no es cancelado
      let estadoFinal = estadoManual;
      if (estadoManual !== 'cancelado') {
        estadoFinal = calcularEstadoTaller(
          new Date(data.fecha),
          data.horaInicio,
          data.horaTermino,
          estadoManual
        );
      }

      const updateData = {
        ...taller, // Mantener datos existentes
        nombre: data.nombre,
        fecha: new Date(data.fecha),
        horaInicio: data.horaInicio,
        horaTermino: data.horaTermino,
        lugar: data.lugar,
        direccion: data.direccion,
        ciudad: data.ciudad,
        organizadorPrincipal: data.organizadorPrincipal,
        responsable: {
          nombre: data.responsableNombre,
          cargo: data.responsableCargo,
          correo: data.responsableCorreo || '',
          telefono: data.responsableTelefono || ''
        },
        tipoEvento: data.tipoEvento,
        enlaceVirtual: (data.tipoEvento === 'virtual' || data.tipoEvento === 'hibrido') ? (data.enlaceVirtual || '') : '',
        objetivo: data.objetivo,
        publicoObjetivo: data.publicoObjetivo,
        descripcionGeneral: data.descripcionGeneral,
        cupoMaximo: parseInt(data.cupoMaximo),
        tipoCosto: data.tipoCosto,
        precio: data.tipoCosto === 'pago' ? parseFloat(data.precio) : 0,
        moneda: data.tipoCosto === 'pago' ? data.moneda : '',
        creditos: parseInt(data.creditos) || 0,
        estado: estadoFinal,
        bannerUrl: bannerUrl,
        fechaModificacion: new Date()
      };

      await updateDoc(docRef, updateData);
      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Taller actualizado correctamente',
        confirmButtonColor: '#3085d6'
      }).then(() => {
        navigate('/admin-talleres');
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al actualizar taller',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading) {
    return (
      <>
        <UserNav />
        <PageTitle
          title="Editar Taller"
          subtitle="Cargando datos del taller..."
        />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Cargando datos del taller..." subMessage="Por favor espera" />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />
      <PageTitle
        title="Editar Taller"
        subtitle="Actualiza la información del taller"
      />

      <section className="gray-simple">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Información Básica */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Información Básica</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Nombre del Evento *</label>
                          <input {...register('nombre')} className="form-control" />
                          {errors.nombre && <p className="text-danger small">{errors.nombre.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Fecha del Evento *</label>
                          <input type="date" {...register('fecha')} className="form-control" />
                          {errors.fecha && <p className="text-danger small">{errors.fecha.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Inicio *</label>
                          <input {...register('horaInicio')} className="form-control" placeholder="09:00" />
                          {errors.horaInicio && <p className="text-danger small">{errors.horaInicio.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Término *</label>
                          <input {...register('horaTermino')} className="form-control" placeholder="18:00" />
                          {errors.horaTermino && <p className="text-danger small">{errors.horaTermino.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <div className="photo-upload">
                            <label>Banner del Evento</label>
                            {currentBannerUrl && (
                              <div className="mb-2">
                                <img
                                  src={currentBannerUrl}
                                  alt="Banner actual"
                                  className="img-thumbnail"
                                  style={{ maxHeight: '150px' }}
                                />
                                <p className="small text-muted">Banner actual</p>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                            />
                            <small className="text-muted">
                              {currentBannerUrl ? 'Selecciona una nueva imagen para reemplazar el banner actual' : 'Agrega un banner promocional'}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Estado del Evento */}
                  <div className="card mb-4">
                    <div className="card-header bg-warning">
                      <h5 className="mb-0"><i className="fa-solid fa-circle-info me-2"></i>Estado del Evento</h5>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-info mb-3">
                        <strong>ℹ️ Nota:</strong> El estado se actualiza automáticamente basándose en las fechas del evento:
                        <ul className="mb-0 mt-2">
                          <li><strong>Próximo:</strong> Cuando la fecha del evento aún no ha llegado</li>
                          <li><strong>En Curso:</strong> Durante el día y horario del evento</li>
                          <li><strong>Finalizado:</strong> Después de que termina el evento</li>
                          <li><strong>Cancelado:</strong> Solo puede establecerse manualmente</li>
                        </ul>
                      </div>

                      <div className="row">
                        <div className="col-lg-6 mb-3">
                          <label className="form-label fw-bold">Estado Actual</label>
                          <select
                            className="form-select"
                            value={estadoManual}
                            onChange={(e) => setEstadoManual(e.target.value)}
                          >
                            <option value="proximo">📅 Próximo</option>
                            <option value="en_curso">▶️ En Curso</option>
                            <option value="finalizado">✅ Finalizado</option>
                            <option value="cancelado">❌ Cancelado</option>
                          </select>
                          <small className="text-muted">
                            {estadoManual === 'cancelado'
                              ? '⚠️ Este taller está marcado como cancelado'
                              : '✨ El estado se actualizará automáticamente al guardar según las fechas'}
                          </small>
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label className="form-label fw-bold">Vista Previa del Estado</label>
                          <div>
                            {estadoManual === 'proximo' && (
                              <span className="badge bg-primary fs-6">📅 Próximo</span>
                            )}
                            {estadoManual === 'en_curso' && (
                              <span className="badge bg-success fs-6">▶️ En Curso</span>
                            )}
                            {estadoManual === 'finalizado' && (
                              <span className="badge bg-secondary fs-6">✅ Finalizado</span>
                            )}
                            {estadoManual === 'cancelado' && (
                              <span className="badge bg-danger fs-6">❌ Cancelado</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ubicación */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Ubicación</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Lugar/Sede *</label>
                          <input {...register('lugar')} className="form-control" />
                          {errors.lugar && <p className="text-danger small">{errors.lugar.message}</p>}
                        </div>

                        <div className="col-lg-8 mb-3">
                          <label>Dirección *</label>
                          <input {...register('direccion')} className="form-control" />
                          {errors.direccion && <p className="text-danger small">{errors.direccion.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Ciudad *</label>
                          <input {...register('ciudad')} className="form-control" />
                          {errors.ciudad && <p className="text-danger small">{errors.ciudad.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organización */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Organización</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Organizador Principal *</label>
                          <input {...register('organizadorPrincipal')} className="form-control" />
                          {errors.organizadorPrincipal && <p className="text-danger small">{errors.organizadorPrincipal.message}</p>}
                        </div>

                        <div className="col-12"><h6>Responsable del Evento</h6></div>

                        <div className="col-lg-6 mb-3">
                          <label>Nombre *</label>
                          <input {...register('responsableNombre')} className="form-control" />
                          {errors.responsableNombre && <p className="text-danger small">{errors.responsableNombre.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Cargo *</label>
                          <input {...register('responsableCargo')} className="form-control" />
                          {errors.responsableCargo && <p className="text-danger small">{errors.responsableCargo.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Correo (opcional)</label>
                          <input type="email" {...register('responsableCorreo')} className="form-control" />
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Teléfono (opcional)</label>
                          <input {...register('responsableTelefono')} className="form-control" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detalles del Evento */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Detalles del Evento</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-4 mb-3">
                          <label>Tipo de Evento *</label>
                          <select
                            {...register('tipoEvento')}
                            className="form-control"
                            onChange={(e) => setTipoEventoActual(e.target.value)}
                          >
                            <option value="presencial">Presencial</option>
                            <option value="virtual">Virtual</option>
                            <option value="hibrido">Híbrido</option>
                          </select>
                          {errors.tipoEvento && <p className="text-danger small">{errors.tipoEvento.message}</p>}
                        </div>

                        {/* Campo de enlace virtual - solo visible para eventos virtuales o híbridos */}
                        {(tipoEventoActual === 'virtual' || tipoEventoActual === 'hibrido') && (
                          <div className="col-lg-8 mb-3">
                            <label>
                              Enlace de la Sesión Virtual {tipoEventoActual === 'virtual' ? '*' : '(opcional)'}
                            </label>
                            <input
                              {...register('enlaceVirtual')}
                              className="form-control"
                              placeholder="https://zoom.us/j/... o https://teams.microsoft.com/..."
                            />
                            <small className="text-muted">
                              Link de Zoom, Teams, Google Meet u otra plataforma
                            </small>
                            {errors.enlaceVirtual && <p className="text-danger small">{errors.enlaceVirtual.message}</p>}
                          </div>
                        )}

                        <div className="col-lg-4 mb-3">
                          <label>Cupo Máximo *</label>
                          <input type="number" {...register('cupoMaximo')} className="form-control" />
                          {errors.cupoMaximo && <p className="text-danger small">{errors.cupoMaximo.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Créditos AMPI</label>
                          <input type="number" {...register('creditos')} className="form-control" defaultValue="0" />
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Objetivo del Evento *</label>
                          <textarea {...register('objetivo')} className="form-control" rows={3} />
                          {errors.objetivo && <p className="text-danger small">{errors.objetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Público Objetivo *</label>
                          <input {...register('publicoObjetivo')} className="form-control" />
                          {errors.publicoObjetivo && <p className="text-danger small">{errors.publicoObjetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Descripción General *</label>
                          <textarea {...register('descripcionGeneral')} className="form-control" rows={4} />
                          {errors.descripcionGeneral && <p className="text-danger small">{errors.descripcionGeneral.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Costo */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Costo</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-4 mb-3">
                          <label>Tipo de Costo *</label>
                          <select {...register('tipoCosto')} className="form-control">
                            <option value="gratuito">Gratuito</option>
                            <option value="pago">Con costo</option>
                          </select>
                          {errors.tipoCosto && <p className="text-danger small">{errors.tipoCosto.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Precio</label>
                          <input type="number" {...register('precio')} className="form-control" />
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Moneda</label>
                          <select {...register('moneda')} className="form-control">
                            <option value="MXN">MXN</option>
                            <option value="USD">USD</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mt-4">
                    <button type="submit" className="btn btn-primary btn-lg me-3">
                      Guardar Cambios
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-lg"
                      onClick={() => navigate('/admin-talleres')}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}